import React from 'react';

/** Detect dark mode from Tailwind `dark` class on <html> */
export function useIsDark(): boolean {
  const [dark, setDark] = React.useState(() => document.documentElement.classList.contains('dark'));
  React.useEffect(() => {
    const observer = new MutationObserver(() => {
      setDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return dark;
}

interface AuthBackgroundProps {
  /** 登录页单独布局插画；授权页默认沿用原背景内嵌插画。 */
  showIllustration?: boolean;
  /** 玻璃登录卡片固定浅色，其余调用方继续跟随系统主题。 */
  forceLight?: boolean;
}

/** 复用现有科技线框背景，允许将机器人插画交由页面独立排版。 */
export const LightBackground: React.FC<AuthBackgroundProps> = ({ showIllustration = true, forceLight = false }) => {
  const [hoveredChip, setHoveredChip] = React.useState<string | null>(null);
  const themeDark = useIsDark();
  const dark = !forceLight && themeDark;

  // Color palette: light vs dark
  const bg = dark
    ? 'linear-gradient(135deg, #0f1729 0%, #131d33 50%, #0f1729 100%)'
    : 'linear-gradient(135deg, #d8e0f0 0%, #cdd7eb 50%, #d5ddf0 100%)';
  const primaryColor = dark ? '#7b9df7' : '#365EEC';
  const secondaryColor = dark ? '#6688f0' : '#2240D9';
  const accentColor = dark ? '#b8caff' : '#93AAFD';
  // Dark mode needs stronger opacity & thicker strokes
  const oMul = dark ? 2.2 : 1;   // opacity multiplier
  const wMul = dark ? 1.8 : 1;   // stroke-width multiplier

  const starColor = dark ? '#a8c4f0' : '#7a9ad4';
  const nebulaColor1 = dark ? '#365EEC' : '#365EEC';
  const nebulaColor2 = dark ? '#6B21A8' : '#7C3AED';

  return (
  <>
    <div className="absolute inset-0" style={{ background: bg }} />
    <svg
      className="absolute inset-0 w-full h-full"
      viewBox="0 0 1440 900"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
    >
      {/* ===== Cosmic / Space elements ===== */}

      {/* Nebula glow — top right */}
      <defs>
        <radialGradient id="nebula1" cx="80%" cy="15%" r="30%">
          <stop offset="0%" stopColor={nebulaColor1} stopOpacity={dark ? 0.08 : 0.04} />
          <stop offset="100%" stopColor={nebulaColor1} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="nebula2" cx="20%" cy="80%" r="25%">
          <stop offset="0%" stopColor={nebulaColor2} stopOpacity={dark ? 0.06 : 0.03} />
          <stop offset="100%" stopColor={nebulaColor2} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="nebula3" cx="60%" cy="50%" r="20%">
          <stop offset="0%" stopColor={nebulaColor1} stopOpacity={dark ? 0.05 : 0.02} />
          <stop offset="100%" stopColor={nebulaColor1} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="1440" height="900" fill="url(#nebula1)" />
      <rect width="1440" height="900" fill="url(#nebula2)" />
      <rect width="1440" height="900" fill="url(#nebula3)" />

      {/* Stars — tiny dots scattered */}
      {[
        [120, 60], [280, 30], [450, 80], [620, 45], [780, 25], [950, 70], [1100, 40], [1250, 85], [1380, 50],
        [80, 160], [310, 180], [530, 140], [700, 170], [860, 130], [1050, 155], [1200, 175], [1350, 120],
        [160, 280], [400, 260], [580, 290], [750, 250], [930, 285], [1150, 270], [1320, 240],
        [200, 400], [370, 380], [540, 420], [710, 390], [880, 410], [1080, 430], [1280, 390],
        [100, 520], [340, 540], [490, 510], [660, 530], [840, 550], [1000, 520], [1180, 545], [1370, 530],
        [180, 650], [350, 680], [520, 640], [690, 670], [870, 660], [1060, 690], [1240, 650], [1400, 680],
        [240, 780], [420, 800], [600, 770], [780, 810], [950, 790], [1140, 770], [1300, 800],
        [70, 850], [300, 830], [550, 860], [820, 840], [1050, 860], [1260, 845],
      ].map(([cx, cy], i) => (
        <circle key={`star-${i}`} cx={cx} cy={cy} r={i % 5 === 0 ? 1.2 : i % 3 === 0 ? 0.8 : 0.5}
          fill={starColor} fillOpacity={(i % 5 === 0 ? 0.5 : i % 3 === 0 ? 0.35 : 0.2) * oMul}
        >
          {i % 4 === 0 && (
            <animate attributeName="fill-opacity"
              values={`${0.15 * oMul};${0.5 * oMul};${0.15 * oMul}`}
              dur={`${3 + (i % 5)}s`} repeatCount="indefinite" />
          )}
        </circle>
      ))}


      {/* Orbit ring — planet-like ellipse */}
      <ellipse cx="1100" cy="350" rx="120" ry="40" fill="none"
        stroke={primaryColor} strokeOpacity={0.04 * oMul} strokeWidth={0.5 * wMul} strokeDasharray="4 6">
        <animateTransform attributeName="transform" type="rotate" from="0 1100 350" to="360 1100 350" dur="60s" repeatCount="indefinite" />
      </ellipse>
      {/* Orbiting dot */}
      <circle r={2 * wMul} fill={primaryColor} fillOpacity={0.2 * oMul}>
        <animateMotion dur="15s" repeatCount="indefinite">
          <mpath href="#orbitPath1" />
        </animateMotion>
      </circle>
      <defs>
        <ellipse id="orbitPath1" cx="1100" cy="350" rx="120" ry="40" />
      </defs>

      {/* Smaller orbit */}
      <ellipse cx="300" cy="750" rx="70" ry="25" fill="none"
        stroke={secondaryColor} strokeOpacity={0.03 * oMul} strokeWidth={0.4 * wMul} strokeDasharray="3 5">
        <animateTransform attributeName="transform" type="rotate" from="0 300 750" to="-360 300 750" dur="45s" repeatCount="indefinite" />
      </ellipse>

      {/* Constellation lines — subtle connecting stars */}
      <path d="M1100,40 L1250,85 L1200,175" fill="none" stroke={starColor} strokeOpacity={0.08 * oMul} strokeWidth={0.3 * wMul} />
      <path d="M280,30 L310,180 L160,280" fill="none" stroke={starColor} strokeOpacity={0.06 * oMul} strokeWidth={0.3 * wMul} />
      <path d="M950,790 L1060,690 L1140,770" fill="none" stroke={starColor} strokeOpacity={0.06 * oMul} strokeWidth={0.3 * wMul} />

      {/* ===== Spiral galaxy — top-left area ===== */}
      <g transform="translate(200, 100)">
        {/* Galaxy core glow */}
        <circle cx="0" cy="0" r="6" fill={starColor} fillOpacity={0.15 * oMul}>
          <animate attributeName="fill-opacity" values={`${0.12 * oMul};${0.22 * oMul};${0.12 * oMul}`} dur="5s" repeatCount="indefinite" />
        </circle>
        <circle cx="0" cy="0" r="14" fill={starColor} fillOpacity={0.04 * oMul} />
        {/* Spiral arms */}
        <path d="M0,0 Q20,-15 35,-8 Q50,0 40,18 Q30,35 10,30 Q-10,25 -20,10 Q-28,-8 -15,-25 Q0,-40 25,-35 Q50,-28 55,-5 Q60,20 45,42" fill="none"
          stroke={starColor} strokeOpacity={0.1 * oMul} strokeWidth={0.5 * wMul}>
          <animateTransform attributeName="transform" type="rotate" from="0 0 0" to="360 0 0" dur="80s" repeatCount="indefinite" />
        </path>
        <path d="M0,0 Q-20,15 -35,8 Q-50,0 -40,-18 Q-30,-35 -10,-30 Q10,-25 20,-10 Q28,8 15,25 Q0,40 -25,35 Q-50,28 -55,5 Q-60,-20 -45,-42" fill="none"
          stroke={starColor} strokeOpacity={0.08 * oMul} strokeWidth={0.4 * wMul}>
          <animateTransform attributeName="transform" type="rotate" from="0 0 0" to="360 0 0" dur="80s" repeatCount="indefinite" />
        </path>
        {/* Tiny stars around galaxy */}
        {[[30, -20], [-25, 15], [15, 30], [-35, -10], [40, 10], [-10, -35], [20, 40], [-40, 25]].map(([x, y], i) => (
          <circle key={`gstar-${i}`} cx={x} cy={y} r={i % 3 === 0 ? 1 : 0.6} fill={starColor} fillOpacity={(i % 2 === 0 ? 0.3 : 0.15) * oMul}>
            {i % 3 === 0 && <animate attributeName="fill-opacity" values={`${0.1 * oMul};${0.35 * oMul};${0.1 * oMul}`} dur={`${3 + i}s`} repeatCount="indefinite" />}
          </circle>
        ))}
      </g>

      {/* ===== Left side — vertical nebula strip + star cluster ===== */}
      {/* Nebula glow on left edge */}
      <defs>
        <radialGradient id="nebulaLeft" cx="0%" cy="50%" r="15%">
          <stop offset="0%" stopColor={nebulaColor2} stopOpacity={dark ? 0.1 : 0.04} />
          <stop offset="100%" stopColor={nebulaColor2} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="200" height="900" fill="url(#nebulaLeft)" />

      {/* Star cluster — left side scattered */}
      {[
        [25, 250], [40, 320], [15, 400], [50, 460], [30, 530], [55, 590], [20, 650], [45, 710],
        [60, 280], [10, 370], [55, 440], [35, 510], [50, 620], [18, 680],
      ].map(([cx, cy], i) => (
        <circle key={`lstar-${i}`} cx={cx} cy={cy} r={i % 4 === 0 ? 1.3 : i % 3 === 0 ? 0.9 : 0.5}
          fill={starColor} fillOpacity={(i % 4 === 0 ? 0.4 : i % 3 === 0 ? 0.25 : 0.15) * oMul}
        >
          {i % 3 === 0 && <animate attributeName="fill-opacity" values={`${0.12 * oMul};${0.4 * oMul};${0.12 * oMul}`} dur={`${4 + (i % 4)}s`} repeatCount="indefinite" />}
        </circle>
      ))}

      {/* Orbit ring — left side */}
      <ellipse cx="35" cy="450" rx="30" ry="80" fill="none"
        stroke={secondaryColor} strokeOpacity={0.05 * oMul} strokeWidth={0.4 * wMul} strokeDasharray="2 4">
        <animateTransform attributeName="transform" type="rotate" from="0 35 450" to="360 35 450" dur="50s" repeatCount="indefinite" />
      </ellipse>

      {/* ===== Bottom-left — planet + rings ===== */}
      <g transform="translate(300, 760)">
        {/* Planet body */}
        <circle cx="0" cy="0" r="18" fill={nebulaColor1} fillOpacity={0.06 * oMul} stroke={primaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.6 * wMul} />
        {/* Planet highlight */}
        <circle cx="-5" cy="-5" r="12" fill={starColor} fillOpacity={0.03 * oMul} />
        {/* Planet ring */}
        <ellipse cx="0" cy="0" rx="35" ry="8" fill="none"
          stroke={primaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.5 * wMul}
          transform="rotate(-20)">
        </ellipse>
        <ellipse cx="0" cy="0" rx="42" ry="10" fill="none"
          stroke={primaryColor} strokeOpacity={0.05 * oMul} strokeWidth={0.4 * wMul}
          transform="rotate(-20)">
        </ellipse>
        {/* Moons */}
        <circle r={1.5 * wMul} fill={starColor} fillOpacity={0.3 * oMul}>
          <animateMotion dur="12s" repeatCount="indefinite">
            <mpath href="#moonOrbit1" />
          </animateMotion>
        </circle>
        <defs>
          <ellipse id="moonOrbit1" cx="0" cy="0" rx="50" ry="14" />
        </defs>
      </g>

      {/* Bottom strip — more stars */}
      {[
        [80, 740], [150, 770], [220, 750], [400, 780], [480, 760], [560, 775],
        [100, 790], [200, 810], [350, 800], [500, 785], [620, 800],
      ].map(([cx, cy], i) => (
        <circle key={`bstar-${i}`} cx={cx} cy={cy} r={i % 3 === 0 ? 1.1 : 0.6}
          fill={starColor} fillOpacity={(i % 3 === 0 ? 0.35 : 0.18) * oMul}
        >
          {i % 4 === 0 && <animate attributeName="fill-opacity" values={`${0.1 * oMul};${0.35 * oMul};${0.1 * oMul}`} dur={`${3.5 + (i % 3)}s`} repeatCount="indefinite" />}
        </circle>
      ))}

      {/* Small galaxy — bottom left */}
      <g transform="translate(130, 780) scale(0.6)">
        <circle cx="0" cy="0" r="4" fill={starColor} fillOpacity={0.12 * oMul} />
        <path d="M0,0 Q15,-10 25,-5 Q35,2 28,15 Q20,25 5,20 Q-8,15 -15,2 Q-18,-12 -5,-20 Q10,-26 25,-18" fill="none"
          stroke={starColor} strokeOpacity={0.07 * oMul} strokeWidth={0.4 * wMul}>
          <animateTransform attributeName="transform" type="rotate" from="0 0 0" to="360 0 0" dur="60s" repeatCount="indefinite" />
        </path>
      </g>

      {/* Top-left — additional constellation */}
      <path d="M120,60 L80,160 L160,280 L200,100" fill="none" stroke={starColor} strokeOpacity={0.05 * oMul} strokeWidth={0.3 * wMul} strokeDasharray="2 3" />
      {/* Cosmic dust arc — top */}
      <path d="M50,80 Q200,40 400,70" fill="none" stroke={starColor} strokeOpacity={0.04 * oMul} strokeWidth={0.4 * wMul} strokeDasharray="1 4">
        <animate attributeName="stroke-dashoffset" values="0;-10" dur="8s" repeatCount="indefinite" />
      </path>

      {/* Hexagons */}
      <polygon points="180,150 200,138 220,150 220,174 200,186 180,174" fill="none" stroke={primaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.8 * wMul}>
        <animate attributeName="stroke-opacity" values={`${0.1 * oMul};${0.25 * oMul};${0.1 * oMul}`} dur="4s" repeatCount="indefinite" />
      </polygon>
      <polygon points="140,350 155,341 170,350 170,368 155,377 140,368" fill="none" stroke={primaryColor} strokeOpacity={0.08 * oMul} strokeWidth={0.7 * wMul} />
      <polygon points="750,680 770,668 790,680 790,704 770,716 750,704" fill="none" stroke={secondaryColor} strokeOpacity={0.09 * oMul} strokeWidth={0.8 * wMul}>
        <animate attributeName="stroke-opacity" values={`${0.09 * oMul};${0.2 * oMul};${0.09 * oMul}`} dur="5s" repeatCount="indefinite" />
      </polygon>
      <polygon points="500,100 514,92 528,100 528,116 514,124 500,116" fill="none" stroke={primaryColor} strokeOpacity={0.07 * oMul} strokeWidth={0.7 * wMul} />

      {/* Circles with pulse */}
      <circle cx="320" cy="200" r="20" fill="none" stroke={primaryColor} strokeOpacity={0.08 * oMul} strokeWidth={0.6 * wMul}>
        <animate attributeName="r" values="20;26;20" dur="6s" repeatCount="indefinite" />
        <animate attributeName="stroke-opacity" values={`${0.08 * oMul};${0.18 * oMul};${0.08 * oMul}`} dur="6s" repeatCount="indefinite" />
      </circle>
      <circle cx="680" cy="750" r="16" fill="none" stroke={secondaryColor} strokeOpacity={0.07 * oMul} strokeWidth={0.6 * wMul}>
        <animate attributeName="r" values="16;22;16" dur="5s" repeatCount="indefinite" />
        <animate attributeName="stroke-opacity" values={`${0.07 * oMul};${0.16 * oMul};${0.07 * oMul}`} dur="5s" repeatCount="indefinite" />
      </circle>
      <circle cx="850" cy="300" r="12" fill="none" stroke={accentColor} strokeOpacity={0.1 * oMul} strokeWidth={0.6 * wMul}>
        <animate attributeName="r" values="12;17;12" dur="7s" repeatCount="indefinite" />
        <animate attributeName="stroke-opacity" values={`${0.1 * oMul};${0.22 * oMul};${0.1 * oMul}`} dur="7s" repeatCount="indefinite" />
      </circle>

      {/* Rotating diamond */}
      <rect x="610" y="400" width="18" height="18" rx="2" fill="none" stroke={primaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.7 * wMul}>
        <animateTransform attributeName="transform" type="rotate" from="0 619 409" to="360 619 409" dur="20s" repeatCount="indefinite" />
      </rect>
      <rect x="100" y="700" width="14" height="14" rx="1.5" fill="none" stroke={secondaryColor} strokeOpacity={0.08 * oMul} strokeWidth={0.6 * wMul}>
        <animateTransform attributeName="transform" type="rotate" from="0 107 707" to="360 107 707" dur="25s" repeatCount="indefinite" />
      </rect>

      {/* Small floating dots */}
      <circle cx="420" cy="250" r={2 * wMul} fill={primaryColor} fillOpacity={0.12 * oMul}>
        <animate attributeName="cy" values="250;240;250" dur="4s" repeatCount="indefinite" />
      </circle>
      <circle cx="250" cy="600" r={1.5 * wMul} fill={secondaryColor} fillOpacity={0.1 * oMul}>
        <animate attributeName="cy" values="600;590;600" dur="3.5s" repeatCount="indefinite" />
      </circle>
      <circle cx="900" cy="500" r={2 * wMul} fill={accentColor} fillOpacity={0.12 * oMul}>
        <animate attributeName="cy" values="500;492;500" dur="5s" repeatCount="indefinite" />
      </circle>
      <circle cx="550" cy="800" r={1.5 * wMul} fill={primaryColor} fillOpacity={0.08 * oMul}>
        <animate attributeName="cy" values="800;793;800" dur="4.5s" repeatCount="indefinite" />
      </circle>
      <circle cx="780" cy="150" r={1.5 * wMul} fill={secondaryColor} fillOpacity={0.1 * oMul}>
        <animate attributeName="cy" values="150;142;150" dur="3s" repeatCount="indefinite" />
      </circle>

      {/* Triangle */}
      <polygon points="450,550 465,525 480,550" fill="none" stroke={primaryColor} strokeOpacity={0.08 * oMul} strokeWidth={0.7 * wMul}>
        <animate attributeName="stroke-opacity" values={`${0.08 * oMul};${0.2 * oMul};${0.08 * oMul}`} dur="6s" repeatCount="indefinite" />
      </polygon>

      {/* Mid-area decorative lines */}
      <line x1="720" y1="350" x2="960" y2="310" stroke={primaryColor} strokeOpacity={0.08 * oMul} strokeWidth={0.6 * wMul} strokeDasharray="6 5">
        <animate attributeName="stroke-dashoffset" values="0;-22" dur="3s" repeatCount="indefinite" />
      </line>
      <line x1="700" y1="550" x2="960" y2="530" stroke={primaryColor} strokeOpacity={0.06 * oMul} strokeWidth={0.6 * wMul} strokeDasharray="6 5">
        <animate attributeName="stroke-dashoffset" values="0;-22" dur="4s" repeatCount="indefinite" />
      </line>

      {/* ===== AI Chip element — mid area (main chip) ===== */}
      <g
        transform="translate(800, 420)"
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setHoveredChip('main')}
        onMouseLeave={() => setHoveredChip(null)}
      >
        {/* Hover detection area */}
        <rect x="-55" y="-55" width="110" height="110" fill="transparent" />
        {/* Chip body */}
        <rect x="-32" y="-32" width="64" height="64" rx="8"
          fill={primaryColor} fillOpacity={(hoveredChip === 'main' ? 0.14 : 0.06) * oMul}
          stroke={primaryColor} strokeOpacity={(hoveredChip === 'main' ? 0.3 : 0.15) * oMul} strokeWidth={1 * wMul}
          style={{ transition: 'fill-opacity 0.4s, stroke-opacity 0.4s' }}
        >
          {!hoveredChip && <animate attributeName="fill-opacity" values={`${0.06 * oMul};${0.12 * oMul};${0.06 * oMul}`} dur="4s" repeatCount="indefinite" />}
        </rect>
        {/* Inner circuit square */}
        <rect x="-18" y="-18" width="36" height="36" rx="4" fill="none"
          stroke={primaryColor} strokeOpacity={(hoveredChip === 'main' ? 0.25 : 0.12) * oMul} strokeWidth={0.8 * wMul}
          style={{ transition: 'stroke-opacity 0.4s' }}
        />
        {/* AI text */}
        <text x="0" y="5" textAnchor="middle" fontSize="12" fontWeight="600"
          fill={primaryColor} fillOpacity={(hoveredChip === 'main' ? 0.5 : 0.25) * oMul}
          style={{ transition: 'fill-opacity 0.4s' }}
        >AI</text>
        {/* Chip pins — top */}
        <line x1="-16" y1="-32" x2="-16" y2="-46" stroke={primaryColor} strokeOpacity={0.12 * oMul} strokeWidth={0.8 * wMul} />
        <line x1="0" y1="-32" x2="0" y2="-50" stroke={primaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.8 * wMul} />
        <line x1="16" y1="-32" x2="16" y2="-46" stroke={primaryColor} strokeOpacity={0.12 * oMul} strokeWidth={0.8 * wMul} />
        {/* Chip pins — bottom */}
        <line x1="-16" y1="32" x2="-16" y2="46" stroke={primaryColor} strokeOpacity={0.12 * oMul} strokeWidth={0.8 * wMul} />
        <line x1="0" y1="32" x2="0" y2="50" stroke={primaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.8 * wMul} />
        <line x1="16" y1="32" x2="16" y2="46" stroke={primaryColor} strokeOpacity={0.12 * oMul} strokeWidth={0.8 * wMul} />
        {/* Chip pins — left */}
        <line x1="-32" y1="-16" x2="-46" y2="-16" stroke={primaryColor} strokeOpacity={0.12 * oMul} strokeWidth={0.8 * wMul} />
        <line x1="-32" y1="0" x2="-50" y2="0" stroke={primaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.8 * wMul} />
        <line x1="-32" y1="16" x2="-46" y2="16" stroke={primaryColor} strokeOpacity={0.12 * oMul} strokeWidth={0.8 * wMul} />
        {/* Chip pins — right */}
        <line x1="32" y1="-16" x2="46" y2="-16" stroke={primaryColor} strokeOpacity={0.12 * oMul} strokeWidth={0.8 * wMul} />
        <line x1="32" y1="0" x2="50" y2="0" stroke={primaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.8 * wMul} />
        <line x1="32" y1="16" x2="46" y2="16" stroke={primaryColor} strokeOpacity={0.12 * oMul} strokeWidth={0.8 * wMul} />
        {/* Pin end dots */}
        <circle cx="-16" cy="-46" r={2 * wMul} fill={primaryColor} fillOpacity={0.18 * oMul} />
        <circle cx="0" cy="-50" r={2 * wMul} fill={primaryColor} fillOpacity={0.15 * oMul} />
        <circle cx="16" cy="-46" r={2 * wMul} fill={primaryColor} fillOpacity={0.18 * oMul} />
        <circle cx="-16" cy="46" r={2 * wMul} fill={primaryColor} fillOpacity={0.18 * oMul} />
        <circle cx="0" cy="50" r={2 * wMul} fill={primaryColor} fillOpacity={0.15 * oMul} />
        <circle cx="16" cy="46" r={2 * wMul} fill={primaryColor} fillOpacity={0.18 * oMul} />
        {/* Pulse ring around chip */}
        <rect x="-40" y="-40" width="80" height="80" rx="12" fill="none"
          stroke={primaryColor} strokeOpacity={(hoveredChip === 'main' ? 0.25 : 0.08) * oMul} strokeWidth={0.6 * wMul}
          style={{ transition: 'stroke-opacity 0.4s' }}
        >
          {!hoveredChip && <animate attributeName="stroke-opacity" values={`${0.08 * oMul};${0.18 * oMul};${0.08 * oMul}`} dur="3s" repeatCount="indefinite" />}
        </rect>
      </g>

      {/* Small AI chip — top right area */}
      <g
        transform="translate(920, 260) scale(0.6)"
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setHoveredChip('topRight')}
        onMouseLeave={() => setHoveredChip(null)}
      >
        <rect x="-30" y="-30" width="60" height="60" fill="transparent" />
        <rect x="-24" y="-24" width="48" height="48" rx="6"
          fill={secondaryColor} fillOpacity={(hoveredChip === 'topRight' ? 0.12 : 0.05) * oMul}
          stroke={secondaryColor} strokeOpacity={(hoveredChip === 'topRight' ? 0.25 : 0.1) * oMul} strokeWidth={0.8 * wMul}
          style={{ transition: 'fill-opacity 0.4s, stroke-opacity 0.4s' }}
        />
        <rect x="-12" y="-12" width="24" height="24" rx="3" fill="none" stroke={secondaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.7 * wMul} />
        <line x1="-10" y1="-24" x2="-10" y2="-34" stroke={secondaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.7 * wMul} />
        <line x1="10" y1="-24" x2="10" y2="-34" stroke={secondaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.7 * wMul} />
        <line x1="-10" y1="24" x2="-10" y2="34" stroke={secondaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.7 * wMul} />
        <line x1="10" y1="24" x2="10" y2="34" stroke={secondaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.7 * wMul} />
        <line x1="-24" y1="-10" x2="-34" y2="-10" stroke={secondaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.7 * wMul} />
        <line x1="-24" y1="10" x2="-34" y2="10" stroke={secondaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.7 * wMul} />
        <line x1="24" y1="-10" x2="34" y2="-10" stroke={secondaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.7 * wMul} />
        <line x1="24" y1="10" x2="34" y2="10" stroke={secondaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.7 * wMul} />
      </g>

      {/* Small AI chip — bottom mid area */}
      <g
        transform="translate(760, 620) scale(0.5)"
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setHoveredChip('bottomMid')}
        onMouseLeave={() => setHoveredChip(null)}
      >
        <rect x="-30" y="-30" width="60" height="60" fill="transparent" />
        <rect x="-24" y="-24" width="48" height="48" rx="6"
          fill={accentColor} fillOpacity={(hoveredChip === 'bottomMid' ? 0.14 : 0.06) * oMul}
          stroke={accentColor} strokeOpacity={(hoveredChip === 'bottomMid' ? 0.25 : 0.1) * oMul} strokeWidth={0.8 * wMul}
          style={{ transition: 'fill-opacity 0.4s, stroke-opacity 0.4s' }}
        >
          {!hoveredChip && <animate attributeName="fill-opacity" values={`${0.06 * oMul};${0.12 * oMul};${0.06 * oMul}`} dur="5s" repeatCount="indefinite" />}
        </rect>
        <line x1="0" y1="-24" x2="0" y2="-36" stroke={accentColor} strokeOpacity={0.1 * oMul} strokeWidth={0.7 * wMul} />
        <line x1="0" y1="24" x2="0" y2="36" stroke={accentColor} strokeOpacity={0.1 * oMul} strokeWidth={0.7 * wMul} />
        <line x1="-24" y1="0" x2="-36" y2="0" stroke={accentColor} strokeOpacity={0.1 * oMul} strokeWidth={0.7 * wMul} />
        <line x1="24" y1="0" x2="36" y2="0" stroke={accentColor} strokeOpacity={0.1 * oMul} strokeWidth={0.7 * wMul} />
      </g>

      {/* ===== Circuit trace lines connecting chips ===== */}
      {/* Main → top-right chip */}
      <path d="M850,420 L920,420 L920,290" fill="none"
        stroke={primaryColor} strokeOpacity={(hoveredChip === 'main' || hoveredChip === 'topRight' ? 0.25 : 0.1) * oMul}
        strokeWidth={(hoveredChip === 'main' || hoveredChip === 'topRight' ? 1.2 : 0.7) * wMul}
        strokeDasharray="4 4"
        style={{ transition: 'stroke-opacity 0.4s, stroke-width 0.4s' }}
      >
        <animate attributeName="stroke-dashoffset" values="0;-16" dur={hoveredChip === 'main' || hoveredChip === 'topRight' ? '1s' : '3s'} repeatCount="indefinite" />
      </path>
      {/* Main → bottom-mid chip */}
      <path d="M800,470 L800,580 L760,620" fill="none"
        stroke={primaryColor} strokeOpacity={(hoveredChip === 'main' || hoveredChip === 'bottomMid' ? 0.25 : 0.08) * oMul}
        strokeWidth={(hoveredChip === 'main' || hoveredChip === 'bottomMid' ? 1.2 : 0.7) * wMul}
        strokeDasharray="4 4"
        style={{ transition: 'stroke-opacity 0.4s, stroke-width 0.4s' }}
      >
        <animate attributeName="stroke-dashoffset" values="0;-16" dur={hoveredChip === 'main' || hoveredChip === 'bottomMid' ? '1s' : '4s'} repeatCount="indefinite" />
      </path>
      {/* Main → left (toward trapezoid) */}
      <path d="M750,420 L700,420 L680,450" fill="none"
        stroke={primaryColor} strokeOpacity={(hoveredChip === 'main' ? 0.22 : 0.08) * oMul}
        strokeWidth={(hoveredChip === 'main' ? 1.2 : 0.7) * wMul}
        strokeDasharray="4 4"
        style={{ transition: 'stroke-opacity 0.4s, stroke-width 0.4s' }}
      >
        <animate attributeName="stroke-dashoffset" values="0;-16" dur={hoveredChip === 'main' ? '1.2s' : '3.5s'} repeatCount="indefinite" />
      </path>
      {/* Main → right (toward login card) */}
      <path d="M855,400 L950,380 L1010,370" fill="none"
        stroke={primaryColor} strokeOpacity={(hoveredChip === 'main' ? 0.2 : 0.07) * oMul}
        strokeWidth={(hoveredChip === 'main' ? 1.1 : 0.6) * wMul}
        strokeDasharray="5 4"
        style={{ transition: 'stroke-opacity 0.4s, stroke-width 0.4s' }}
      >
        <animate attributeName="stroke-dashoffset" values="0;-18" dur={hoveredChip === 'main' ? '1s' : '3s'} repeatCount="indefinite" />
      </path>
      {/* Main → top-left diagonal */}
      <path d="M770,390 L740,350 L720,310" fill="none"
        stroke={secondaryColor} strokeOpacity={(hoveredChip === 'main' ? 0.2 : 0.07) * oMul}
        strokeWidth={(hoveredChip === 'main' ? 1.1 : 0.6) * wMul}
        strokeDasharray="3 4"
        style={{ transition: 'stroke-opacity 0.4s, stroke-width 0.4s' }}
      >
        <animate attributeName="stroke-dashoffset" values="0;-14" dur={hoveredChip === 'main' ? '1.2s' : '4s'} repeatCount="indefinite" />
      </path>
      {/* Main → bottom-right */}
      <path d="M835,450 L880,500 L920,540" fill="none"
        stroke={primaryColor} strokeOpacity={(hoveredChip === 'main' ? 0.2 : 0.07) * oMul}
        strokeWidth={(hoveredChip === 'main' ? 1.1 : 0.6) * wMul}
        strokeDasharray="4 3"
        style={{ transition: 'stroke-opacity 0.4s, stroke-width 0.4s' }}
      >
        <animate attributeName="stroke-dashoffset" values="0;-14" dur={hoveredChip === 'main' ? '1s' : '3.5s'} repeatCount="indefinite" />
      </path>
      {/* Top-right chip → right extension */}
      <path d="M940,260 L980,260 L1010,250" fill="none"
        stroke={secondaryColor} strokeOpacity={(hoveredChip === 'topRight' ? 0.22 : 0.07) * oMul}
        strokeWidth={(hoveredChip === 'topRight' ? 1.1 : 0.6) * wMul}
        strokeDasharray="3 3"
        style={{ transition: 'stroke-opacity 0.4s, stroke-width 0.4s' }}
      >
        <animate attributeName="stroke-dashoffset" values="0;-12" dur={hoveredChip === 'topRight' ? '1s' : '3s'} repeatCount="indefinite" />
      </path>
      {/* Bottom-mid chip → left extension */}
      <path d="M748,620 L710,630 L680,650" fill="none"
        stroke={accentColor} strokeOpacity={(hoveredChip === 'bottomMid' ? 0.22 : 0.07) * oMul}
        strokeWidth={(hoveredChip === 'bottomMid' ? 1.1 : 0.6) * wMul}
        strokeDasharray="3 3"
        style={{ transition: 'stroke-opacity 0.4s, stroke-width 0.4s' }}
      >
        <animate attributeName="stroke-dashoffset" values="0;-12" dur={hoveredChip === 'bottomMid' ? '1s' : '3s'} repeatCount="indefinite" />
      </path>

      {/* ===== Additional geometric shapes ===== */}
      {/* Dotted circle */}
      <circle cx="880" cy="500" r="28" fill="none" stroke={primaryColor} strokeOpacity={0.07 * oMul} strokeWidth={0.6 * wMul} strokeDasharray="3 3">
        <animateTransform attributeName="transform" type="rotate" from="0 880 500" to="360 880 500" dur="30s" repeatCount="indefinite" />
      </circle>
      {/* Cross shape */}
      <line x1="730" y1="300" x2="750" y2="300" stroke={secondaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.7 * wMul} />
      <line x1="740" y1="290" x2="740" y2="310" stroke={secondaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.7 * wMul} />
      {/* Small pentagon */}
      <polygon points="870,650 880,644 890,648 888,660 874,660" fill="none" stroke={primaryColor} strokeOpacity={0.08 * oMul} strokeWidth={0.6 * wMul}>
        <animate attributeName="stroke-opacity" values={`${0.08 * oMul};${0.18 * oMul};${0.08 * oMul}`} dur="5s" repeatCount="indefinite" />
      </polygon>
      {/* Concentric squares */}
      <g transform="translate(940, 550)">
        <rect x="-10" y="-10" width="20" height="20" rx="2" fill="none" stroke={primaryColor} strokeOpacity={0.08 * oMul} strokeWidth={0.6 * wMul}>
          <animateTransform attributeName="transform" type="rotate" from="0 0 0" to="360 0 0" dur="18s" repeatCount="indefinite" />
        </rect>
        <rect x="-16" y="-16" width="32" height="32" rx="3" fill="none" stroke={primaryColor} strokeOpacity={0.05 * oMul} strokeWidth={0.5 * wMul}>
          <animateTransform attributeName="transform" type="rotate" from="0 0 0" to="-360 0 0" dur="24s" repeatCount="indefinite" />
        </rect>
      </g>
      {/* Extra hexagon near main chip */}
      <polygon points="860,340 874,332 888,340 888,356 874,364 860,356" fill="none" stroke={primaryColor} strokeOpacity={0.06 * oMul} strokeWidth={0.6 * wMul}>
        <animate attributeName="stroke-opacity" values={`${0.06 * oMul};${0.14 * oMul};${0.06 * oMul}`} dur="5s" repeatCount="indefinite" />
      </polygon>
      {/* Small cross near bottom chip */}
      <line x1="800" y1="660" x2="814" y2="660" stroke={accentColor} strokeOpacity={0.08 * oMul} strokeWidth={0.6 * wMul} />
      <line x1="807" y1="653" x2="807" y2="667" stroke={accentColor} strokeOpacity={0.08 * oMul} strokeWidth={0.6 * wMul} />
      {/* Small triangle near top-right chip */}
      <polygon points="960,230 968,218 976,230" fill="none" stroke={secondaryColor} strokeOpacity={0.07 * oMul} strokeWidth={0.6 * wMul} />

      {/* ===== GPU Chip — top center-right (area 1) ===== */}
      <g
        transform="translate(870, 180)"
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setHoveredChip('gpu')}
        onMouseLeave={() => setHoveredChip(null)}
      >
        <rect x="-50" y="-40" width="100" height="80" fill="transparent" />
        {/* GPU chip body */}
        <rect x="-36" y="-28" width="72" height="56" rx="6"
          fill={primaryColor} fillOpacity={(hoveredChip === 'gpu' ? 0.15 : 0.06) * oMul}
          stroke={primaryColor} strokeOpacity={(hoveredChip === 'gpu' ? 0.35 : 0.15) * oMul} strokeWidth={1 * wMul}
          style={{ transition: 'fill-opacity 0.4s, stroke-opacity 0.4s' }}
        />
        {/* GPU inner grid (processor cores) */}
        {[[-22, -16], [-6, -16], [10, -16], [-22, 0], [-6, 0], [10, 0]].map(([x, y]) => (
          <rect key={`gc-${x}-${y}`} x={x} y={y} width="12" height="10" rx="1.5" fill="none"
            stroke={primaryColor} strokeOpacity={(hoveredChip === 'gpu' ? 0.3 : 0.12) * oMul} strokeWidth={0.6 * wMul} />
        ))}
        {/* GPU label */}
        <text x="0" y="-32" textAnchor="middle" fontSize="9" fontWeight="500"
          fill={primaryColor} fillOpacity={(hoveredChip === 'gpu' ? 0.5 : 0.25) * oMul}
          style={{ transition: 'fill-opacity 0.4s', fontFamily: 'monospace' }}
        >GPU</text>
        {/* GPU pins — top & bottom */}
        {[-24, -12, 0, 12, 24].map(px => (
          <React.Fragment key={`gpu-pin-${px}`}>
            <line x1={px} y1={-28} x2={px} y2={-38} stroke={primaryColor} strokeOpacity={0.12 * oMul} strokeWidth={0.7 * wMul} />
            <line x1={px} y1={28} x2={px} y2={38} stroke={primaryColor} strokeOpacity={0.12 * oMul} strokeWidth={0.7 * wMul} />
          </React.Fragment>
        ))}
        {/* GPU pins — left & right */}
        {[-14, 0, 14].map(py => (
          <React.Fragment key={`gpu-pin-lr-${py}`}>
            <line x1={-36} y1={py} x2={-44} y2={py} stroke={primaryColor} strokeOpacity={0.12 * oMul} strokeWidth={0.7 * wMul} />
            <line x1={36} y1={py} x2={44} y2={py} stroke={primaryColor} strokeOpacity={0.12 * oMul} strokeWidth={0.7 * wMul} />
          </React.Fragment>
        ))}
        {/* Outer ring */}
        <rect x="-46" y="-38" width="92" height="76" rx="10" fill="none"
          stroke={primaryColor} strokeOpacity={(hoveredChip === 'gpu' ? 0.25 : 0.08) * oMul} strokeWidth={0.6 * wMul}
          style={{ transition: 'stroke-opacity 0.4s' }}
        />
      </g>

      {/* ===== LLM Chip — top right corner (area 2) ===== */}
      <g
        transform="translate(1380, 160)"
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setHoveredChip('llm')}
        onMouseLeave={() => setHoveredChip(null)}
      >
        <rect x="-40" y="-35" width="80" height="70" fill="transparent" />
        {/* LLM body — brain/neural network shape */}
        <rect x="-30" y="-24" width="60" height="48" rx="8"
          fill={secondaryColor} fillOpacity={(hoveredChip === 'llm' ? 0.14 : 0.05) * oMul}
          stroke={secondaryColor} strokeOpacity={(hoveredChip === 'llm' ? 0.3 : 0.12) * oMul} strokeWidth={0.8 * wMul}
          style={{ transition: 'fill-opacity 0.4s, stroke-opacity 0.4s' }}
        />
        {/* Neural network nodes */}
        <circle cx="-14" cy="-8" r="4" fill="none" stroke={secondaryColor} strokeOpacity={(hoveredChip === 'llm' ? 0.3 : 0.12) * oMul} strokeWidth={0.6 * wMul} />
        <circle cx="14" cy="-8" r="4" fill="none" stroke={secondaryColor} strokeOpacity={(hoveredChip === 'llm' ? 0.3 : 0.12) * oMul} strokeWidth={0.6 * wMul} />
        <circle cx="0" cy="8" r="4" fill="none" stroke={secondaryColor} strokeOpacity={(hoveredChip === 'llm' ? 0.3 : 0.12) * oMul} strokeWidth={0.6 * wMul} />
        {/* Neural connections */}
        <line x1="-10" y1="-8" x2="10" y2="-8" stroke={secondaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.5 * wMul} />
        <line x1="-12" y1="-5" x2="-2" y2="5" stroke={secondaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.5 * wMul} />
        <line x1="12" y1="-5" x2="2" y2="5" stroke={secondaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.5 * wMul} />
        {/* LLM label */}
        <text x="0" y="-28" textAnchor="middle" fontSize="8" fontWeight="500"
          fill={secondaryColor} fillOpacity={(hoveredChip === 'llm' ? 0.5 : 0.25) * oMul}
          style={{ transition: 'fill-opacity 0.4s', fontFamily: 'monospace' }}
        >LLM</text>
        {/* Pins */}
        {[-16, 0, 16].map(px => (
          <React.Fragment key={`llm-pin-${px}`}>
            <line x1={px} y1={-24} x2={px} y2={-32} stroke={secondaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.6 * wMul} />
            <line x1={px} y1={24} x2={px} y2={32} stroke={secondaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.6 * wMul} />
          </React.Fragment>
        ))}
      </g>

      {/* ===== NPU Chip — right middle (area 3) ===== */}
      <g
        transform="translate(940, 580)"
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setHoveredChip('npu')}
        onMouseLeave={() => setHoveredChip(null)}
      >
        <rect x="-40" y="-35" width="80" height="70" fill="transparent" />
        {/* NPU body */}
        <rect x="-30" y="-24" width="60" height="48" rx="6"
          fill={accentColor} fillOpacity={(hoveredChip === 'npu' ? 0.14 : 0.05) * oMul}
          stroke={accentColor} strokeOpacity={(hoveredChip === 'npu' ? 0.3 : 0.12) * oMul} strokeWidth={0.8 * wMul}
          style={{ transition: 'fill-opacity 0.4s, stroke-opacity 0.4s' }}
        />
        {/* NPU inner — tensor grid */}
        {[[-16, -12], [-2, -12], [-16, 2], [-2, 2]].map(([x, y]) => (
          <rect key={`npu-g-${x}-${y}`} x={x} y={y} width="10" height="10" rx="1" fill="none"
            stroke={accentColor} strokeOpacity={(hoveredChip === 'npu' ? 0.28 : 0.1) * oMul} strokeWidth={0.5 * wMul} />
        ))}
        {/* Arrow inside — inference direction */}
        <path d="M16,-6 L22,0 L16,6" fill="none" stroke={accentColor} strokeOpacity={(hoveredChip === 'npu' ? 0.3 : 0.12) * oMul} strokeWidth={0.7 * wMul} />
        {/* NPU label */}
        <text x="0" y="-28" textAnchor="middle" fontSize="8" fontWeight="500"
          fill={accentColor} fillOpacity={(hoveredChip === 'npu' ? 0.5 : 0.25) * oMul}
          style={{ transition: 'fill-opacity 0.4s', fontFamily: 'monospace' }}
        >NPU</text>
        {/* Pins */}
        {[-16, 0, 16].map(px => (
          <React.Fragment key={`npu-pin-${px}`}>
            <line x1={px} y1={-24} x2={px} y2={-34} stroke={accentColor} strokeOpacity={0.1 * oMul} strokeWidth={0.6 * wMul} />
            <line x1={px} y1={24} x2={px} y2={34} stroke={accentColor} strokeOpacity={0.1 * oMul} strokeWidth={0.6 * wMul} />
          </React.Fragment>
        ))}
        {[-10, 10].map(py => (
          <React.Fragment key={`npu-pin-lr-${py}`}>
            <line x1={-30} y1={py} x2={-38} y2={py} stroke={accentColor} strokeOpacity={0.1 * oMul} strokeWidth={0.6 * wMul} />
            <line x1={30} y1={py} x2={38} y2={py} stroke={accentColor} strokeOpacity={0.1 * oMul} strokeWidth={0.6 * wMul} />
          </React.Fragment>
        ))}
      </g>

      {/* ===== Model Chip — bottom center (area 4) ===== */}
      <g
        transform="translate(760, 760)"
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setHoveredChip('model')}
        onMouseLeave={() => setHoveredChip(null)}
      >
        <rect x="-45" y="-35" width="90" height="70" fill="transparent" />
        {/* Model body */}
        <rect x="-34" y="-24" width="68" height="48" rx="7"
          fill={primaryColor} fillOpacity={(hoveredChip === 'model' ? 0.14 : 0.05) * oMul}
          stroke={primaryColor} strokeOpacity={(hoveredChip === 'model' ? 0.3 : 0.12) * oMul} strokeWidth={0.8 * wMul}
          style={{ transition: 'fill-opacity 0.4s, stroke-opacity 0.4s' }}
        />
        {/* Model layers — stacked rectangles */}
        <rect x="-20" y="-14" width="40" height="6" rx="1.5" fill="none" stroke={primaryColor} strokeOpacity={(hoveredChip === 'model' ? 0.28 : 0.1) * oMul} strokeWidth={0.5 * wMul} />
        <rect x="-18" y="-4" width="36" height="6" rx="1.5" fill="none" stroke={primaryColor} strokeOpacity={(hoveredChip === 'model' ? 0.25 : 0.09) * oMul} strokeWidth={0.5 * wMul} />
        <rect x="-16" y="6" width="32" height="6" rx="1.5" fill="none" stroke={primaryColor} strokeOpacity={(hoveredChip === 'model' ? 0.22 : 0.08) * oMul} strokeWidth={0.5 * wMul} />
        {/* Model label */}
        <text x="0" y="-28" textAnchor="middle" fontSize="8" fontWeight="500"
          fill={primaryColor} fillOpacity={(hoveredChip === 'model' ? 0.5 : 0.25) * oMul}
          style={{ transition: 'fill-opacity 0.4s', fontFamily: 'monospace' }}
        >MODEL</text>
        {/* Pins */}
        {[-20, -6, 6, 20].map(px => (
          <React.Fragment key={`model-pin-${px}`}>
            <line x1={px} y1={-24} x2={px} y2={-34} stroke={primaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.6 * wMul} />
            <line x1={px} y1={24} x2={px} y2={34} stroke={primaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.6 * wMul} />
          </React.Fragment>
        ))}
      </g>

      {/* ===== Connection lines from illustration (trapezoid right edge ~700,400) to new chips ===== */}
      {/* Trapezoid → GPU */}
      <path d="M700,300 Q780,240 870,180" fill="none"
        stroke={primaryColor} strokeOpacity={(hoveredChip === 'gpu' ? 0.3 : 0.1) * oMul}
        strokeWidth={(hoveredChip === 'gpu' ? 1.4 : 0.8) * wMul}
        strokeDasharray="5 4"
        style={{ transition: 'stroke-opacity 0.4s, stroke-width 0.4s' }}
      >
        <animate attributeName="stroke-dashoffset" values="0;-18" dur={hoveredChip === 'gpu' ? '0.8s' : '3s'} repeatCount="indefinite" />
      </path>
      {hoveredChip === 'gpu' && (
        <circle r={3 * wMul} fill={primaryColor} fillOpacity={0.6 * oMul}>
          <animateMotion dur="1.2s" repeatCount="indefinite" path="M700,300 Q780,240 870,180" />
        </circle>
      )}

      {/* Trapezoid → LLM */}
      <path d="M700,280 Q950,160 1380,160" fill="none"
        stroke={secondaryColor} strokeOpacity={(hoveredChip === 'llm' ? 0.28 : 0.08) * oMul}
        strokeWidth={(hoveredChip === 'llm' ? 1.2 : 0.7) * wMul}
        strokeDasharray="6 5"
        style={{ transition: 'stroke-opacity 0.4s, stroke-width 0.4s' }}
      >
        <animate attributeName="stroke-dashoffset" values="0;-22" dur={hoveredChip === 'llm' ? '1s' : '4s'} repeatCount="indefinite" />
      </path>
      {hoveredChip === 'llm' && (
        <circle r={2.5 * wMul} fill={secondaryColor} fillOpacity={0.55 * oMul}>
          <animateMotion dur="1.8s" repeatCount="indefinite" path="M700,280 Q950,160 1380,160" />
        </circle>
      )}

      {/* Trapezoid → NPU */}
      <path d="M680,550 Q800,570 940,580" fill="none"
        stroke={accentColor} strokeOpacity={(hoveredChip === 'npu' ? 0.3 : 0.09) * oMul}
        strokeWidth={(hoveredChip === 'npu' ? 1.3 : 0.7) * wMul}
        strokeDasharray="4 4"
        style={{ transition: 'stroke-opacity 0.4s, stroke-width 0.4s' }}
      >
        <animate attributeName="stroke-dashoffset" values="0;-16" dur={hoveredChip === 'npu' ? '0.8s' : '3s'} repeatCount="indefinite" />
      </path>
      {hoveredChip === 'npu' && (
        <circle r={3 * wMul} fill={accentColor} fillOpacity={0.6 * oMul}>
          <animateMotion dur="1.2s" repeatCount="indefinite" path="M680,550 Q800,570 940,580" />
        </circle>
      )}

      {/* Trapezoid → Model */}
      <path d="M620,700 Q690,740 760,760" fill="none"
        stroke={primaryColor} strokeOpacity={(hoveredChip === 'model' ? 0.3 : 0.09) * oMul}
        strokeWidth={(hoveredChip === 'model' ? 1.3 : 0.7) * wMul}
        strokeDasharray="4 4"
        style={{ transition: 'stroke-opacity 0.4s, stroke-width 0.4s' }}
      >
        <animate attributeName="stroke-dashoffset" values="0;-16" dur={hoveredChip === 'model' ? '0.8s' : '3s'} repeatCount="indefinite" />
      </path>
      {hoveredChip === 'model' && (
        <circle r={3 * wMul} fill={primaryColor} fillOpacity={0.6 * oMul}>
          <animateMotion dur="1s" repeatCount="indefinite" path="M620,700 Q690,740 760,760" />
        </circle>
      )}

      {/* Inter-chip connections — GPU ↔ LLM */}
      <path d="M920,180 Q1100,140 1350,160" fill="none"
        stroke={primaryColor} strokeOpacity={(hoveredChip === 'gpu' || hoveredChip === 'llm' ? 0.22 : 0.06) * oMul}
        strokeWidth={(hoveredChip === 'gpu' || hoveredChip === 'llm' ? 1 : 0.5) * wMul}
        strokeDasharray="3 4"
        style={{ transition: 'stroke-opacity 0.4s, stroke-width 0.4s' }}
      >
        <animate attributeName="stroke-dashoffset" values="0;-14" dur="3.5s" repeatCount="indefinite" />
      </path>

      {/* GPU ↔ AI main chip */}
      <path d="M870,220 Q840,320 800,380" fill="none"
        stroke={primaryColor} strokeOpacity={(hoveredChip === 'gpu' || hoveredChip === 'main' ? 0.22 : 0.06) * oMul}
        strokeWidth={(hoveredChip === 'gpu' || hoveredChip === 'main' ? 1 : 0.5) * wMul}
        strokeDasharray="3 4"
        style={{ transition: 'stroke-opacity 0.4s, stroke-width 0.4s' }}
      >
        <animate attributeName="stroke-dashoffset" values="0;-14" dur="3s" repeatCount="indefinite" />
      </path>

      {/* NPU ↔ Model */}
      <path d="M920,610 Q860,680 790,760" fill="none"
        stroke={accentColor} strokeOpacity={(hoveredChip === 'npu' || hoveredChip === 'model' ? 0.22 : 0.06) * oMul}
        strokeWidth={(hoveredChip === 'npu' || hoveredChip === 'model' ? 1 : 0.5) * wMul}
        strokeDasharray="3 4"
        style={{ transition: 'stroke-opacity 0.4s, stroke-width 0.4s' }}
      >
        <animate attributeName="stroke-dashoffset" values="0;-14" dur="3.5s" repeatCount="indefinite" />
      </path>

      {/* ===== Sandbox Chip — bottom right area ===== */}
      <g
        transform="translate(920, 690)"
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setHoveredChip('sandbox')}
        onMouseLeave={() => setHoveredChip(null)}
      >
        <rect x="-55" y="-35" width="110" height="70" fill="transparent" />
        {/* Chip body */}
        <rect x="-38" y="-24" width="76" height="48" rx="7"
          fill={primaryColor} fillOpacity={(hoveredChip === 'sandbox' ? 0.14 : 0.06) * oMul}
          stroke={primaryColor} strokeOpacity={(hoveredChip === 'sandbox' ? 0.3 : 0.15) * oMul} strokeWidth={1 * wMul}
          style={{ transition: 'fill-opacity 0.4s, stroke-opacity 0.4s' }}
        >
          {!hoveredChip && <animate attributeName="fill-opacity" values={`${0.06 * oMul};${0.12 * oMul};${0.06 * oMul}`} dur="5s" repeatCount="indefinite" />}
        </rect>
        {/* Shield icon */}
        <path d="M-2,-12 L-10,-8 L-10,0 C-10,6 -2,12 -2,12 C-2,12 6,6 6,0 L6,-8 Z" fill="none"
          stroke={primaryColor} strokeOpacity={(hoveredChip === 'sandbox' ? 0.5 : 0.2) * oMul} strokeWidth={1.2 * wMul}
          style={{ transition: 'stroke-opacity 0.4s' }}
        />
        {/* Check mark inside shield */}
        <path d="M-4,0 L-1,3 L4,-2" fill="none"
          stroke={primaryColor} strokeOpacity={(hoveredChip === 'sandbox' ? 0.5 : 0.2) * oMul} strokeWidth={1 * wMul} strokeLinecap="round" strokeLinejoin="round"
          style={{ transition: 'stroke-opacity 0.4s' }}
        />
        {/* Pins — top */}
        {[-22, -8, 8, 22].map(px => (
          <React.Fragment key={`sandbox-pin-t-${px}`}>
            <line x1={px} y1={-24} x2={px} y2={-34} stroke={primaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.6 * wMul} />
            <circle cx={px} cy={-34} r={1.5 * wMul} fill={primaryColor} fillOpacity={0.15 * oMul} />
          </React.Fragment>
        ))}
        {/* Pins — bottom */}
        {[-22, -8, 8, 22].map(px => (
          <React.Fragment key={`sandbox-pin-b-${px}`}>
            <line x1={px} y1={24} x2={px} y2={34} stroke={primaryColor} strokeOpacity={0.1 * oMul} strokeWidth={0.6 * wMul} />
            <circle cx={px} cy={34} r={1.5 * wMul} fill={primaryColor} fillOpacity={0.15 * oMul} />
          </React.Fragment>
        ))}
        {/* Label */}
        <text x="-2" y="-28" textAnchor="middle" fontSize="8" fontWeight="500"
          fill={primaryColor} fillOpacity={(hoveredChip === 'sandbox' ? 0.55 : 0.25) * oMul}
          style={{ transition: 'fill-opacity 0.4s', fontFamily: 'monospace' }}
        >SANDBOX</text>
        {/* Pulse ring */}
        <rect x="-44" y="-30" width="88" height="60" rx="10" fill="none"
          stroke={primaryColor} strokeOpacity={(hoveredChip === 'sandbox' ? 0.25 : 0.08) * oMul} strokeWidth={0.6 * wMul}
          style={{ transition: 'stroke-opacity 0.4s' }}
        >
          {!hoveredChip && <animate attributeName="stroke-opacity" values={`${0.08 * oMul};${0.18 * oMul};${0.08 * oMul}`} dur="4s" repeatCount="indefinite" />}
        </rect>
      </g>

      {/* Sandbox ↔ Model connection */}
      <path d="M880,690 Q830,720 790,760" fill="none"
        stroke={primaryColor} strokeOpacity={(hoveredChip === 'sandbox' || hoveredChip === 'model' ? 0.25 : 0.08) * oMul}
        strokeWidth={(hoveredChip === 'sandbox' || hoveredChip === 'model' ? 1.2 : 0.6) * wMul}
        strokeDasharray="4 4"
        style={{ transition: 'stroke-opacity 0.4s, stroke-width 0.4s' }}
      >
        <animate attributeName="stroke-dashoffset" values="0;-16" dur="3s" repeatCount="indefinite" />
      </path>

      {showIllustration && <RobotScene dark={dark} />}
    </svg>
  </>
  );
};


/** 原机器人素材与圆角斜切轮廓共用一份定义，避免授权页和登录页视觉分叉。 */
function RobotScene({ dark = false }: { dark?: boolean }) {
  const id = React.useId().replace(/:/g, '');
  const clipId = `robot-clip-${id}`;
  const shimmerId = `robot-shimmer-${id}`;
  const trapFill = dark ? '#1a2744' : '#dfe7f4';
  const trapStroke = dark ? '#3a5580' : '#c5d3e8';
  return <>
      {/* ===== Left trapezoid area with illustration ===== */}
      <defs>
        <clipPath id={clipId}>
          <path d="M120,190 L700,190 Q740,190 732,230 L630,680 Q622,720 582,720 L120,720 Q80,720 80,680 L80,230 Q80,190 120,190 Z" />
        </clipPath>
        {/* Shimmer gradient for light sweep effect */}
        <linearGradient id={shimmerId} x1="0" y1="0" x2="1" y2="0.3">
          <stop offset="0%" stopColor="white" stopOpacity="0" />
          <stop offset="40%" stopColor="white" stopOpacity="0" />
          <stop offset="50%" stopColor="white" stopOpacity="0.8" />
          <stop offset="60%" stopColor="white" stopOpacity="0" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Trapezoid fill — rounded corners */}
      <path d="M120,190 L700,190 Q740,190 732,230 L630,680 Q622,720 582,720 L120,720 Q80,720 80,680 L80,230 Q80,190 120,190 Z" fill={trapFill} fillOpacity="0.5" />
      <path d="M120,190 L700,190 Q740,190 732,230 L630,680 Q622,720 582,720 L120,720 Q80,720 80,680 L80,230 Q80,190 120,190 Z" fill="none" stroke={trapStroke} strokeOpacity="0.4" strokeWidth="1" />

      {/* Image centered inside trapezoid */}
      <g clipPath={`url(#${clipId})`}>
        <image href="/1111.jpg" x="60" y="170" width="700" height="570" preserveAspectRatio="xMidYMid slice" opacity="0.9">
          {/* Floating / breathing movement — more noticeable */}
          <animateTransform attributeName="transform" type="translate" values="0,0; 5,-4; 0,0; -5,4; 0,0" dur="20s" repeatCount="indefinite" />
        </image>
        {/* Shimmer overlay — light sweep */}
        <rect x="60" y="170" width="700" height="570" fill={`url(#${shimmerId})`} opacity="0.2">
          <animateTransform attributeName="transform" type="translate" values="-700,0; 700,0" dur="10s" repeatCount="indefinite" />
        </rect>
      </g>
  </>;
}

/** 按原轮廓比例缩放插画，不拉伸机器人；移动端由布局隐藏。 */
export function LoginIllustration() {
  return <svg className="login-illustration" viewBox="80 190 660 530" aria-hidden="true" focusable="false">
    <RobotScene />
  </svg>;
}
